/**
 * Boost page (/boost) — Feb 2026 web mirror of the mobile Boost screen.
 *
 * Four server-driven states:
 *   A) Active           → amber hero, pulsing icon, live mm:ss countdown
 *   B) Premium w/ boosts→ "Activate Boost Now" CTA + stat strip
 *   C) Premium out      → CTA becomes "Buy more with Premium / VIP"
 *   D) Free tier        → package preview + "Upgrade to Premium" CTA
 *
 * Backend contract:
 *   GET  /api/boost/status   → { active, seconds_remaining, boosts_remaining,
 *                                tier, weekly_quota }
 *   POST /api/boost/activate → { minutes, boost_until, boosts_remaining, ... }
 *                              401/403 for non-premium.
 *
 * Duration is READ from `response.minutes` — never hardcode. If the server
 * bumps it 60 → 90 tomorrow, the UI copy updates automatically via the
 * `serverMinutes` state (falls back to 60 for the pre-activation copy).
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Zap, Crown, Gem, Loader2 } from 'lucide-react';
import { api } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

function formatTime(s) {
  if (s <= 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

export default function BoostPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);      // /boost/status response
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [serverMinutes, setServerMinutes] = useState(60);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    try {
      const r = await api.get('/boost/status');
      setStatus(r.data);
    } catch { /* silent — retry on next focus */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh on tab focus.
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  // Live countdown tick — only when a boost is active. Cheaper than polling
  // the server every second; we recompute seconds_remaining on the client
  // and re-fetch once the countdown hits zero.
  const activeUntilRef = useRef(null);
  useEffect(() => {
    if (!status?.active || !status?.seconds_remaining) {
      activeUntilRef.current = null;
      return;
    }
    activeUntilRef.current = Date.now() + status.seconds_remaining * 1000;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [status?.active, status?.seconds_remaining]);

  const remainingSeconds = activeUntilRef.current
    ? Math.max(0, Math.floor((activeUntilRef.current - now) / 1000))
    : 0;

  // When countdown reaches 0, re-fetch status once so the hero flips.
  useEffect(() => {
    if (status?.active && remainingSeconds === 0 && activeUntilRef.current) {
      activeUntilRef.current = null;
      load();
    }
  }, [remainingSeconds, status?.active, load]);

  const activate = async () => {
    if (activating) return;
    setActivating(true);
    try {
      const r = await api.post('/boost/activate');
      if (r.data?.minutes) setServerMinutes(r.data.minutes);
      toast.success(`Boosted for ${r.data?.minutes || 60} minutes!`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      // Feb 2026 — structured paywall payload → /subscription?feature=&reason=
      if ((status === 403 || status === 429) && detail && typeof detail === 'object' && detail.code) {
        navigate(`/subscription?feature=${detail.feature || 'boost'}&reason=${detail.code}`);
        return;
      }
      const detailStr = String(detail || '').toLowerCase();
      if (status === 401 || status === 403 || detailStr.includes('premium') || detailStr.includes('vip')) {
        setUpgradeOpen(true);
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Activation failed'));
      }
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--cream)] flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]" />
      </div>
    );
  }

  const tier = status?.tier || 'free';
  const isPremium = tier === 'premium' || tier === 'vip';
  const active = !!status?.active;
  const boostsRemaining = status?.boosts_remaining ?? 0;
  const weeklyQuota = status?.weekly_quota ?? 0;

  return (
    <div className="min-h-screen bg-[var(--cream)] pb-[96px]" data-testid="boost-screen">
      {/* Header */}
      <div className="px-5 pt-6 pb-4 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          data-testid="boost-back"
          className="w-9 h-9 rounded-full hover:bg-black/[0.03] flex items-center justify-center"
          aria-label="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="font-brand text-xl font-[600] tracking-tight text-[#0F0F10]">Meet Priority</h1>
          <p className="text-xs text-zinc-500">Get seen by 10× more people</p>
        </div>
      </div>

      {/* Hero card — flips between active + inactive states */}
      <div className="px-5 mt-2">
        {active ? (
          <ActiveHero remainingSeconds={remainingSeconds} minutes={serverMinutes} />
        ) : (
          <InactiveHero minutes={serverMinutes} />
        )}
      </div>

      {/* Stat strip — only when we have quota info to show */}
      {(isPremium || weeklyQuota > 0) && !active && (
        <div className="px-5 mt-3">
          <div
            data-testid="boost-quota"
            className="flex items-center gap-2 rounded-2xl bg-white border border-[var(--hairline)] p-3"
          >
            <StatCell label="Available" value={boostsRemaining} accent="#B91C1C" />
            <div className="w-px h-8 bg-black/[0.06]" />
            <StatCell label="Weekly quota" value={weeklyQuota} accent="#0F0F10" />
            <div className="w-px h-8 bg-black/[0.06]" />
            <StatCell label="Tier" value={tier.toUpperCase()} accent="#B38138" />
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="px-5 mt-4">
        {active ? (
          <p className="text-center text-[13px] text-[#4B4B52]">
            You&apos;re currently boosted. Sit tight — new matches roll in.
          </p>
        ) : isPremium && boostsRemaining > 0 ? (
          <button
            onClick={activate}
            disabled={activating}
            data-testid="boost-activate-btn"
            className="w-full py-3.5 rounded-full brand-gradient text-white text-sm font-semibold btn-shine disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {activating ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} fill="currentColor" />}
            Activate Boost Now
          </button>
        ) : isPremium ? (
          <button
            onClick={() => navigate('/store?highlight=boost')}
            data-testid="boost-buy-more-btn"
            className="w-full py-3.5 rounded-full brand-gradient text-white text-sm font-semibold btn-shine inline-flex items-center justify-center gap-2"
          >
            <Zap size={16} fill="currentColor" />
            Buy more with Premium / VIP
          </button>
        ) : (
          <>
            <div
              data-testid="boost-free-banner"
              className="rounded-2xl bg-[#0F0F10] text-white px-4 py-3 text-[13px] leading-snug mb-3"
            >
              Profile Boost is a Premium & VIP feature.
            </div>
            <button
              onClick={() => navigate('/subscription')}
              data-testid="boost-upgrade-btn"
              className="w-full py-3.5 rounded-full brand-gradient text-white text-sm font-semibold btn-shine inline-flex items-center justify-center gap-2"
            >
              <Crown size={16} />
              Upgrade to Premium
            </button>
          </>
        )}
      </div>

      {/* Package previews (all tiers see them — free = upsell, premium = comparison) */}
      <div className="px-5 mt-8">
        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8A8A93] mb-3">Plans</p>
        <div className="flex flex-col gap-3">
          <PlanCard
            icon={<Zap size={18} className="text-white" fill="currentColor" />}
            iconBg="linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)"
            title="Premium"
            price="$19.99 / mo"
            bullets={['1 boost per week', 'All premium features', 'Undo swipes, see who liked you']}
          />
          <PlanCard
            icon={<Gem size={18} className="text-white" />}
            iconBg="linear-gradient(135deg, #1F2937 0%, #0F172A 100%)"
            title="VIP"
            price="$39.99 / mo"
            bullets={['3 boosts per week', 'Priority placement', 'Concierge dating support']}
          />
        </div>
      </div>

      {/* Upgrade dialog — shown when non-premium user tries to activate */}
      <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <DialogContent className="bg-white border-[var(--hairline)] rounded-2xl p-6 max-w-sm" data-testid="boost-upgrade-dialog">
          <DialogTitle className="font-display text-lg font-[700]">Premium feature</DialogTitle>
          <p className="text-sm text-[#4B4B52] mt-2 mb-5">
            Profile Boost is available on Premium and VIP. Upgrade to unlock.
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setUpgradeOpen(false)}
              className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold text-[#4B4B52] hover:bg-black/[0.04]"
            >
              Maybe later
            </button>
            <button
              onClick={() => { setUpgradeOpen(false); navigate('/subscription'); }}
              data-testid="boost-see-plans-btn"
              className="flex-1 px-4 py-2.5 rounded-full brand-gradient text-white text-sm font-semibold btn-shine"
            >
              See plans
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ActiveHero({ remainingSeconds, minutes }) {
  return (
    <div
      className="rounded-3xl p-6 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)' }}
    >
      <div className="flex items-center gap-4">
        <div className="relative w-14 h-14 rounded-full bg-white/25 backdrop-blur flex items-center justify-center">
          <div className="absolute inset-0 rounded-full bg-white/30 animate-ping" style={{animationDuration: '1.4s'}} />
          <Zap size={26} className="text-white relative z-10" fill="currentColor" />
        </div>
        <div className="flex-1">
          <p className="text-white text-[11px] font-bold uppercase tracking-[0.15em] opacity-90">Boost active</p>
          <p className="text-white font-display text-xl font-[700] leading-tight mt-1">
            You&apos;re being shown to 10× more people
          </p>
        </div>
      </div>
      <div
        data-testid="boost-timer"
        className="mt-4 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-black/25 backdrop-blur text-white text-[13px] font-bold tabular-nums"
      >
        ⏱ {formatTime(remainingSeconds)} remaining
        <span className="opacity-75 text-[11px] font-normal">of {minutes} min</span>
      </div>
    </div>
  );
}

function InactiveHero({ minutes }) {
  return (
    <div
      className="rounded-3xl p-6 relative overflow-hidden text-white"
      style={{ background: 'linear-gradient(135deg, #B91C1C 0%, #7F1D1D 100%)' }}
    >
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-white/15 flex items-center justify-center">
          <Zap size={26} className="text-white" fill="currentColor" />
        </div>
        <div className="flex-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] opacity-80">Meet Priority</p>
          <p className="font-display text-xl font-[700] leading-tight mt-1">
            Be seen by 10× more people
          </p>
        </div>
      </div>
      <p className="text-white/80 text-[13px] mt-3 leading-relaxed">
        {minutes}-minute boost · top of Discover · prioritized matches
      </p>
    </div>
  );
}

function StatCell({ label, value, accent }) {
  return (
    <div className="flex-1 text-center">
      <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#8A8A93]">{label}</p>
      <p className="text-lg font-bold mt-0.5" style={{ color: accent }}>{value}</p>
    </div>
  );
}

function PlanCard({ icon, iconBg, title, price, bullets }) {
  return (
    <div className="rounded-2xl bg-white border border-[var(--hairline)] p-4 flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: iconBg }}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <p className="font-brand font-[600] text-[15px] text-[#0F0F10]">{title}</p>
          <p className="font-bold text-[13px] text-[#0F0F10]">{price}</p>
        </div>
        <ul className="text-[12px] text-[#4B4B52] space-y-0.5">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span className="text-[#B91C1C]">·</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
